import React, {
  cloneElement,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
  ReactElement,
} from "react";
import { CalendarPopoverProps } from "./CalendarPopover.types";
import Popover from "../../popover";
import classNames from "classnames";
import { theme } from "../../utils/theme";

export const CalendarPopover: React.FC<CalendarPopoverProps> = ({
  anchorX,
  anchorY,
  children,
  onClickAnchor,
  unmountEvent,
  role,
  anchorElement,
  visibleCount,
  isRangeVariant,
  popoverRef,
  calOpen,
  setCalOpen,
  portalWrapperClassName,
}) => {
  const anchorRef = useRef<HTMLElement | null>(
    null
  ) as MutableRefObject<HTMLElement>;
  const [isActive, setIsActive] = useState(calOpen);

  useEffect(() => {
    if (calOpen) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  }, [calOpen]);

  const clickHandler = (e?: MouseEvent | KeyboardEvent): void => {
    e?.preventDefault();
    setTimeout(
      () => {
        setCalOpen?.(!isActive);
        onClickAnchor?.(!isActive);
        setIsActive(!isActive);
      },
      // Wait 400 ms before popover closes (range variant)
      isActive && isRangeVariant ? 400 : 0
    );
  };

  const disableElements = (value: boolean): void => {
    setIsActive(value);
    onClickAnchor?.(value);
    setCalOpen?.(value);
  };

  const childrenWithModifiedProps = React.Children.map<ReactNode, ReactNode>(
    children,
    (child) => {
      return React.isValidElement(child)
        ? React.cloneElement(child as ReactElement<any>, {
            _privateProps: {
              ...(child.props as any)._privateProps,
              closePopover: clickHandler,
              unmountEvent,
              isActive,
            },
          })
        : null;
    }
  );

  return (
    <Popover
      ref={popoverRef}
      role={role}
      anchorX={anchorX}
      anchorY={anchorY}
      className={classNames(
        "spark-calendar",
        "spark-pad-0",
        portalWrapperClassName
      )}
      toggleElWrapperClassName="spark-input__addon"
      toggleElWrapperStyle={{ right: "1rem" }}
      onClose={(): void => {
        disableElements(false);
      }}
      data-visible-count={String(visibleCount)}
      open={calOpen}
      onOpenChange={setCalOpen}
      toggleEl={cloneElement(anchorElement as ReactElement<any>, {
        ref: anchorRef,
        onClick: clickHandler,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            clickHandler(e);
          } else {
            setIsActive(false);
            setCalOpen?.(false);
          }
        },
        style: { right: 0, color: theme.palette["katana-black"] },
      })}
    >
      {childrenWithModifiedProps}
    </Popover>
  );
};

CalendarPopover.displayName = "CalendarPopover";
